import { useMemo, useState } from "react"
import { Link } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { UserLink } from "@/components/ui/user-link"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { CategoryBucket, EscalationDetailRow } from "@/types/analytics_escalations"

// Bottom-of-page detail table for the Escalation dashboard.
// Filtering is done client-side over the props the controller renders so
// the page stays snappy while clicking through donut categories. The
// filter pills update local state AND lift up via callbacks so the URL
// stays in sync (controller sends back a fresh slice on hard reload).

type OutcomeFilter = string | null

type Props = {
  rows: EscalationDetailRow[]
  categories: CategoryBucket[]
  categoryFilter: string | null
  onCategoryChange: (key: string | null) => void
  outcomeFilter: OutcomeFilter
  onOutcomeChange: (outcome: OutcomeFilter) => void
  query: string
  onQueryChange: (q: string) => void
}

const OUTCOMES: Array<{ key: string; label: string }> = [
  { key: "open",       label: "Open" },
  { key: "escalated",  label: "Escalated" },
  { key: "resolved",   label: "Resolved" },
  { key: "auto_resolved", label: "Auto-resolved" },
  { key: "rage_locked",   label: "Rage-locked" },
]

export function EscalationDetailTable({
  rows, categories,
  categoryFilter, onCategoryChange,
  outcomeFilter, onOutcomeChange,
  query, onQueryChange,
}: Props) {
  const [localQuery, setLocalQuery] = useState(query)

  const filtered = useMemo(() => {
    const q = localQuery.trim().toLowerCase()
    return rows.filter((r) => {
      if (categoryFilter && r.reasonKey !== categoryFilter) return false
      if (outcomeFilter && r.outcome !== outcomeFilter) return false
      if (q) {
        const inName = (r.customerName ?? "").toLowerCase().includes(q)
        const inEmail = (r.customerEmail ?? "").toLowerCase().includes(q)
        const inUuid = r.convoUuid.toLowerCase().includes(q)
        if (!inName && !inEmail && !inUuid) return false
      }
      return true
    })
  }, [rows, localQuery, categoryFilter, outcomeFilter])

  const handleQueryChange = (v: string) => {
    setLocalQuery(v)
    onQueryChange(v)
  }

  return (
    <Card className="overflow-hidden p-0">
      <header className="flex flex-wrap items-center gap-3 border-b border-border p-4">
        <div className="flex-1 min-w-[200px]">
          <Input
            type="search"
            placeholder="Search customer or convo UUID…"
            value={localQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill
            label="All categories"
            active={categoryFilter === null}
            onClick={() => onCategoryChange(null)}
          />
          {categories.map((c) => (
            <FilterPill
              key={c.key}
              label={c.name}
              active={categoryFilter === c.key}
              onClick={() => onCategoryChange(c.key)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill
            label="All outcomes"
            active={outcomeFilter === null}
            onClick={() => onOutcomeChange(null)}
          />
          {OUTCOMES.map((o) => (
            <FilterPill
              key={o.key}
              label={o.label}
              active={outcomeFilter === o.key}
              onClick={() => onOutcomeChange(o.key)}
            />
          ))}
        </div>
      </header>

      {filtered.length === 0 ? (
        <div className="p-8 text-center text-sm text-app-muted">
          No escalations match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-app-subtle">
                <th className="px-4 py-2 text-left">Convo</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-right">Escalated</th>
                <th className="px-4 py-2 text-right">Turns</th>
                <th className="px-4 py-2 text-left">Outcome</th>
                <th className="px-4 py-2 text-center">CSAT</th>
                <th className="px-4 py-2 text-left">Topics</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((r) => (
                <tr key={r.convoUuid} className="text-app-text">
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link
                      href={`/portal/tickets/${r.convoUuid}`}
                      className="text-app-text transition hover:text-brand-red hover:underline"
                    >
                      {r.convoUuid.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <UserLink id={r.appuserId} name={r.customerName} email={r.customerEmail} />
                  </td>
                  <td className="px-4 py-3">
                    {r.reasonName ? <Badge tone="neutral">{r.reasonName}</Badge> : <span className="text-app-subtle">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-app-muted">
                    {fmtCompactRelative(r.escalatedAt)}
                  </td>
                  <td className="px-4 py-3 text-right text-app-muted tabular-nums">{r.turnCount}</td>
                  <td className="px-4 py-3">
                    <Badge tone={toneForOutcome(r.outcome)}>{r.outcome}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center text-base">
                    {r.csat === "good" ? "👍" : r.csat === "bad" ? "👎" : <span className="text-app-subtle">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.topics.length === 0 ? (
                      <span className="text-app-subtle">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {r.topics.map((t) => (
                          <Badge key={t.key} tone="neutral">{t.name}</Badge>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-chip border px-2.5 py-1 text-xs font-medium transition",
        active
          ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
          : "border-border bg-white/5 text-app-muted hover:text-app-text",
      )}
    >
      {label}
    </button>
  )
}

function toneForOutcome(outcome: string): "red" | "warning" | "neutral" | "success" {
  if (outcome === "escalated") return "red"
  if (outcome === "open" || outcome === "rage_locked") return "warning"
  if (outcome === "resolved" || outcome === "auto_resolved") return "success"
  return "neutral"
}
